import type { CSSProperties, ReactElement, ReactNode, RefAttributes } from 'react';
import type { BreakpointNames, ButtonProps } from '@workramp/ui';
import { Children, useEffect, useState, useRef, cloneElement } from 'react';
import { Col, Stack } from '@workramp/ui';
import { LearnerPagination } from '~/elc/features/learner';
import { MixPanel } from '~/common';
import { useViewportBreakpoint } from '~/hooks';
import {
  CarouselRow,
  PaginationWrapper,
  CarouselItemWrapper,
  CarouselWrapper,
  StyledIconLeftButton,
  StyledIconRightButton,
} from './Carousel.styles';

type PaginationDirection = 'next' | 'prev';

export type CarouselMixPanelEventOptions = {
  origin?: string;
  namePrefix: string;
};
export interface CarouselProps {
  className?: string;
  arrowRightEl?: ReactElement<RefAttributes<HTMLButtonElement> & ButtonProps>;
  arrowLeftEl?: ReactElement<RefAttributes<HTMLButtonElement> & ButtonProps>;
  isCentered?: boolean;
  children: ReactNode[];
  height?: CSSProperties['height'];
  showPaginationControls: boolean;
  showButtonControls?: boolean;
  mixPanelEventOptions?: CarouselMixPanelEventOptions;
  pageSizeByBreakpoint?: {
    [key in BreakpointNames]?: number;
  };
}

export const Carousel = ({
  className,
  children,
  height,
  showPaginationControls,
  showButtonControls = false,
  arrowRightEl,
  arrowLeftEl,
  mixPanelEventOptions,
  pageSizeByBreakpoint,
  isCentered = true,
}: CarouselProps) => {
  const viewportBreakpoint = useViewportBreakpoint();
  const arrowRightRef = useRef<HTMLButtonElement>(null);
  const arrowLeftRef = useRef<HTMLButtonElement>(null);

  const clonedArrowRightEl = () => {
    if (arrowRightEl !== undefined) {
      if (arrowRightEl === null) {
        return null;
      }

      return cloneElement(arrowRightEl, {
        ref: arrowRightRef,
        isDisabled: lastResultIndex >= children.length - 1,
        onPress: () => paginationPageChangeHandler(pageNum + 1), // triggerProps should provide the onPress handler but it doesn't
      });
    }

    return (
      <StyledIconRightButton
        iconName="caret-right:regular"
        label="right"
        color="secondary"
        variant="outline"
        onPress={() => paginationPageChangeHandler(pageNum + 1)}
        isDisabled={lastResultIndex >= children.length - 1}
      />
    );
  };

  const clonedArrowLeftEl = () => {
    if (arrowLeftEl !== undefined) {
      if (arrowLeftEl === null) {
        return null;
      }

      return cloneElement(arrowLeftEl, {
        ref: arrowLeftRef,
        isDisabled: firstResultIndex === 0,
        onPress: () => paginationPageChangeHandler(pageNum - 1), // triggerProps should provide the onPress handler but it doesn't
      });
    }

    return (
      <StyledIconLeftButton
        iconName="caret-left:regular"
        color="secondary"
        variant="outline"
        label="left"
        onPress={() => paginationPageChangeHandler(pageNum - 1)}
        isDisabled={firstResultIndex === 0}
      />
    );
  };

  const getCarouselPageSizeForBreakpoint = (breakpoint: BreakpointNames) => {
    if (pageSizeByBreakpoint && pageSizeByBreakpoint[breakpoint]) {
      return pageSizeByBreakpoint[breakpoint];
    }

    const carouselPageSizeByBreakpoint = {
      xs: 2,
      sm: 2,
      md: 3,
      lg: 4,
      xl: 6,
    };
    return carouselPageSizeByBreakpoint[breakpoint];
  };

  const carouselPageSize = getCarouselPageSizeForBreakpoint(viewportBreakpoint);

  const [pageNum, setPageNum] = useState(0);
  const firstResultIndex = pageNum * carouselPageSize;
  const lastResultIndex = Math.min(firstResultIndex + carouselPageSize, children.length) - 1;

  const carouselContentRefs = useRef([]);
  const [paginationDirection, setPaginationDirection] = useState<PaginationDirection | null>(null);

  const handlePagination = () => {
    let indexOfItemToScrollIntoView: number | null = null;
    if (paginationDirection === 'next') {
      indexOfItemToScrollIntoView = lastResultIndex;
    } else if (paginationDirection === 'prev') {
      indexOfItemToScrollIntoView = firstResultIndex;
    }
    if (carouselContentRefs.current[indexOfItemToScrollIntoView]) {
      carouselContentRefs.current[indexOfItemToScrollIntoView].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  useEffect(handlePagination, [carouselContentRefs, paginationDirection, firstResultIndex, lastResultIndex]);

  /*
    When a user resizes the window after pagination scroll, the cards become misaligned.
    This useEffect will reset the alignment and scroll back to the first card.
  */
  useEffect(() => {
    setPageNum(0);
    setPaginationDirection(null);
    const element = carouselContentRefs.current[0];
    const parentElement = element?.parentElement?.parentElement;
    if (parentElement?.scrollTo) {
      parentElement.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }, [carouselPageSize, carouselContentRefs, viewportBreakpoint]);

  const paginationPageChangeHandler = (newPageNum: number) => {
    // The newPageNum will be larger than current page (pageNum) when user clicks NEXT button and smaller when they click PREVIOUS button
    const isNextPage = newPageNum > pageNum;

    setPaginationDirection(isNextPage ? 'next' : 'prev');
    setPageNum(newPageNum);

    if (mixPanelEventOptions === undefined) return;

    if (isNextPage) {
      MixPanel.track(`${mixPanelEventOptions.namePrefix}_pagination_next`, {
        mixPanelEventOrigin: mixPanelEventOptions.origin,
      });
    } else {
      MixPanel.track(`${mixPanelEventOptions.namePrefix}_pagination_prev`, {
        mixPanelEventOrigin: mixPanelEventOptions.origin,
      });
    }
  };

  return (
    <Stack size={0} className={className}>
      <div
        style={{
          position: 'relative',
        }}
      >
        {showButtonControls && clonedArrowRightEl()}
        <CarouselWrapper $isCentered={isCentered}>
          <CarouselRow align="flex-start" gapX={4} justify="flex-start">
            {Children.map(children, (child, i) => (
              <Col
                {...{
                  xs: 12 / getCarouselPageSizeForBreakpoint('xs'),
                  sm: 12 / getCarouselPageSizeForBreakpoint('sm'),
                  md: 12 / getCarouselPageSizeForBreakpoint('md'),
                  lg: 12 / getCarouselPageSizeForBreakpoint('lg'),
                  xl: 12 / getCarouselPageSizeForBreakpoint('xl'),
                }}
              >
                {/* eslint-disable-next-line no-return-assign */}
                <CarouselItemWrapper
                  $isCentered={isCentered}
                  $height={height}
                  ref={(ref) => {
                    carouselContentRefs.current[i] = ref;
                  }}
                >
                  {child}
                </CarouselItemWrapper>
              </Col>
            ))}
          </CarouselRow>
        </CarouselWrapper>
        {showButtonControls && clonedArrowLeftEl()}
      </div>
      {showPaginationControls && (
        <PaginationWrapper>
          <LearnerPagination
            page={pageNum}
            pageSize={carouselPageSize}
            total={children.length}
            onChangePage={paginationPageChangeHandler}
            isPreviousPageDisabled={firstResultIndex === 0}
            isNextPageDisabled={lastResultIndex >= children.length - 1}
          />
        </PaginationWrapper>
      )}
    </Stack>
  );
};
